import { useState, useCallback, useEffect } from 'react';
import {
  DialogContent,
} from '@mui/material';
import {
  CarouselButtons,
  SliderWrapper,
  DotsContainer,
  Dot,
} from './OnboardingImageCarouselIntroductionStyle';
import useEmblaCarousel from 'embla-carousel-react';
import ImageCarousel from '../ImageCarousel/index.jsx';
import PurpleBackgroundBtn from '../Buttons/PurpleBackgroundBtn/index.jsx';
import NoBackgroundAndBorderBtn from '../Buttons/NoBackground&BorderBtn/index.jsx';
import onboardingSlide1 from '../../static/img/onboardingslide-1.svg';
import onboardingSlide2 from '../../static/img/onboardingslide-2.svg';
import onboardingSlide3 from '../../static/img/onboardingslide-3.svg';
import onboardingSlide4 from '../../static/img/onboardingslide-4.svg';
import onboardingSlide5 from '../../static/img/onboardingslide-5.svg';
import onBoardingSlide from '../../static/img/onboarding-slide.svg';
import studentGradesSlide from '../../static/img/onboardingslide-student-grades.svg';

// Carousel slide decks. Teachers (school + non-school) see the full 6-slide
// value-prop deck; the simpler student flow (school + non-school) shares a
// 3-slide deck. Illustrations are reused from the teacher deck where the design
// matches; the student "estimated grades" slide has its own artwork
// (onboardingslide-student-grades.svg).
const TEACHER_SLIDES = [
  { text: 'Save up to 10 hours per week with instant AI suggestions on student work', nextButtonText: 'Next', image: onboardingSlide1 },
  { text: 'Students get instant curriculum-aligned feedback on drafts and classwork set by you', nextButtonText: 'Next', image: onboardingSlide2 },
  { text: "Take back control over student AI use", subtext: "Feedback is tailored to your marking preferences and remains 100% editable before sharing with students", nextButtonText: "Next", image: onboardingSlide3 },
  { text: "Create specific tasks for your classes and use AI to generate personalised feedback in seconds", nextButtonText: "Next", image: onboardingSlide4 },
  { text: "Empower students to do more practice with trusted, teacher-aligned feedback", nextButtonText: "Next", image: onboardingSlide5 },
  { text: "Track student progress and identify skill gaps", nextButtonText: "Finish", image: onBoardingSlide },
];

const STUDENT_SLIDES = [
  { text: 'Submit your work and get expert feedback in seconds', nextButtonText: 'Next', image: onboardingSlide1 },
  { text: 'Get estimated grades based on your specific subject and year level', nextButtonText: 'Next', image: studentGradesSlide },
  { text: 'Generate practice questions any time to prepare for your exams like a pro', nextButtonText: 'Continue', image: onboardingSlide5 },
];

// This component is used to display the onboarding image carousel introduction
const OnboardingImageCarouselIntroduction = ({ onCloseOnboarding, audience = 'teacher' }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false, 
    watchDrag: false 
  });

  const slides = audience === 'student' ? STUDENT_SLIDES : TEACHER_SLIDES;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const handleNextClick = (index) => {
    if (index === slides.length - 1) {
      setIsFinished(true);
      onCloseOnboarding();
      return;
    }
    if (emblaApi) {
      emblaApi.scrollTo(index + 1, true);
    }
  };

  const handleBackClick = (index) => {
    if (emblaApi) {
      emblaApi.scrollTo(currentSlide - 1, true);
    }
  };

  if (isFinished) {
    return null;
  }

  return (
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', padding: 0, overflowY: "hidden", width: "694px", height: "auto", gap: "10px" }}>
      <SliderWrapper>
        {/* Embla Viewport */}
        <div ref={emblaRef} style={{ overflow: 'hidden', width: '100%', height: '100%' }}>
          {/* Embla Container */}
          <div style={{ display: 'flex', transition: 'none'}}>
            {slides.map((slide, index) => (
              <div key={index} style={{ flex: '0 0 100%', minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <ImageCarousel
                    text={slide.text}
                    subtext={slide.subtext}
                    image={slide.image}
                    isNarrow={index === slides.length - 2}  
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </SliderWrapper>

      <CarouselButtons>
        <div style={{ minWidth: '100px', display: 'flex', justifyContent: 'flex-start' }}>
          {currentSlide > 0 && (
            <NoBackgroundAndBorderBtn
              text={"Back"}
              onClick={handleBackClick}
            />
          )}
        </div>

        <DotsContainer>
          {slides.map((_, index) => (
            <Dot
              key={index}
              active={index === currentSlide}
              onClick={() => emblaApi?.scrollTo(index,true)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </DotsContainer>

        <div style={{ minWidth: '100px', display: 'flex', justifyContent: 'flex-end' }}>
          <PurpleBackgroundBtn
            buttonText={slides[currentSlide].nextButtonText}
            onClickFn={() => handleNextClick(currentSlide)}
            showRightIcon={currentSlide !== slides.length - 1}
          />
        </div>
      </CarouselButtons>
    </DialogContent>
  );
};

export default OnboardingImageCarouselIntroduction;
